import React, { useEffect, useState } from 'react';
import { User, Shield, Phone, FileText, Camera, Loader2, Save, Edit2, Mail, CheckCircle2, XCircle, Lock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { userService } from '../services/userService';
import { UserProfileResponse } from '../types';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { PhoneInput } from '../components/ui/phone-input';
import { AtLimit, CharCounter } from '../components/ui/char-counter';
import { showSuccess, showError } from '../components/Alerts';
import LoadingScreen from '../components/ui/LoadingScreen';

const avatarColors = [
  'bg-purple-500', 'bg-blue-500', 'bg-green-500', 'bg-amber-500',
  'bg-rose-500', 'bg-indigo-500', 'bg-teal-500', 'bg-orange-500',
];

function getInitials(name: string | null, email: string): string {
  if (name) {
    const parts = name.trim().split(' ');
    return parts.length >= 2
      ? (parts[0][0] + parts[1][0]).toUpperCase()
      : parts[0].slice(0, 2).toUpperCase();
  }
  return email.slice(0, 2).toUpperCase();
}

function getAvatarColor(id: number): string {
  return avatarColors[id % avatarColors.length];
}

function getRoleDisplayName(roleName: string, isOwner: boolean): string {
  if (isOwner || roleName?.toUpperCase() === 'OWNER') return 'Administrador';
  return roleName;
}

export default function UserProfile() {
  const { user } = useAuth();
  const [profile, setProfile] = useState<UserProfileResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    phoneNumber: '',
    profilePictureUrl: '',
    bio: '',
  });

  const fetchProfile = async () => {
    if (!user) return;
    try {
      setLoading(true);
      const data = await userService.getUserProfile(user.id);
      setProfile(data);
      setFormData({
        name: data.name || '',
        phoneNumber: data.phoneNumber || '',
        profilePictureUrl: data.profilePictureUrl || '',
        bio: data.bio || '',
      });
    } catch {
      showError('Error al cargar el perfil de usuario');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const handleSave = async () => {
    if (!user) return;
    try {
      setSaving(true);
      const updated = await userService.updateUserProfile(user.id, formData);
      setProfile(updated);
      setEditing(false);
      showSuccess('Perfil actualizado correctamente');
    } catch {
      showError('Error al actualizar el perfil');
    } finally {
      setSaving(false);
    }
  };

  const cancelEdit = () => {
    if (profile) {
      setFormData({
        name: profile.name || '',
        phoneNumber: profile.phoneNumber || '',
        profilePictureUrl: profile.profilePictureUrl || '',
        bio: profile.bio || '',
      });
    }
    setEditing(false);
  };

  if (loading) return <LoadingScreen message="Cargando perfil" fullScreen={false} />;

  if (!profile) {
    return (
      <div className="w-full h-full p-6">
        <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
          <div className="py-16 text-center">
            <User className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="mb-2 text-lg font-medium text-foreground">No se pudo cargar el perfil</h3>
            <p className="text-sm text-muted-foreground">Intentá recargar la página.</p>
          </div>
        </div>
      </div>
    );
  }

  const roleDisplayName = getRoleDisplayName(profile.roleName, profile.isOwner);

  return (
    <div className="w-full h-full p-4 space-y-6 sm:p-6">

      {/* Page header */}
      <div className="flex items-center gap-4">
        <div className="flex items-center justify-center flex-shrink-0 bg-indigo-100 w-11 h-11 rounded-xl dark:bg-indigo-900/30">
          <User className="w-5 h-5 text-indigo-600 dark:text-indigo-400" />
        </div>
        <div>
          <h1 className="text-2xl font-bold text-foreground">Mi Perfil</h1>
          <p className="text-sm text-muted-foreground">Información y configuración de tu cuenta</p>
        </div>
      </div>

      {/* Identity card */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">

        {/* Avatar + info + actions */}
        <div className="px-6 py-5 border-b border-border">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">

            {/* Avatar */}
            <div className="flex items-center gap-4">
              <div className="relative flex-shrink-0">
                {profile.profilePictureUrl ? (
                  <img
                    src={profile.profilePictureUrl}
                    alt="Avatar"
                    className="w-16 h-16 rounded-full object-cover ring-2 ring-border"
                  />
                ) : (
                  <div className={`flex items-center justify-center w-16 h-16 rounded-full text-white text-xl font-bold ring-2 ring-border ${getAvatarColor(profile.id)}`}>
                    {getInitials(profile.name, profile.email)}
                  </div>
                )}
                {profile.isActive && (
                  <span className="absolute bottom-0.5 right-0.5 w-3.5 h-3.5 bg-green-500 border-2 border-card rounded-full" title="Activo" />
                )}
              </div>
              <div>
                <h2 className="text-lg font-semibold text-foreground leading-tight">
                  {profile.name || <span className="italic font-normal text-muted-foreground">Sin nombre</span>}
                </h2>
                <p className="text-sm text-muted-foreground">{profile.email}</p>
                <div className="flex items-center gap-2 mt-1.5">
                  {profile.isOwner ? (
                    <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                      <Shield className="w-3 h-3" />
                      Administrador
                    </span>
                  ) : (
                    <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                      {roleDisplayName}
                    </span>
                  )}
                  {profile.isActive ? (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-green-700 dark:text-green-400">
                      <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                      Activo
                    </span>
                  ) : (
                    <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium text-muted-foreground">
                      <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
                      Inactivo
                    </span>
                  )}
                </div>
              </div>
            </div>

            {/* Edit actions */}
            <div className="flex items-center gap-2">
              {!editing ? (
                <Button onClick={() => setEditing(true)} size="sm" className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90">
                  <Edit2 className="w-4 h-4" />
                  Editar Perfil
                </Button>
              ) : (
                <>
                  <Button variant="outline" size="sm" onClick={cancelEdit} disabled={saving} className="rounded-lg">
                    Cancelar
                  </Button>
                  <Button size="sm" onClick={handleSave} disabled={saving} className="flex items-center gap-2 rounded-lg bg-primary text-primary-foreground hover:opacity-90">
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Detail sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 divide-y md:divide-y-0 md:divide-x divide-border">

          {/* Left: Personal info */}
          <div className="p-6 space-y-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Información Personal</p>

            <div className="space-y-4">
              {/* Nombre */}
              <div className="space-y-2">
                <Label htmlFor="name" className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <User className="w-3.5 h-3.5" />
                  Nombre completo
                </Label>
                {editing ? (
                  <>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                      maxLength={255}
                      placeholder="Tu nombre"
                      className="rounded-lg bg-background border-border focus-visible:ring-primary"
                    />
                    <AtLimit value={formData.name} max={255} />
                  </>
                ) : (
                  <div className="flex items-center gap-2 text-sm text-foreground bg-muted/40 px-3 py-2.5 rounded-lg border border-transparent">
                    {profile.name || <span className="text-muted-foreground italic">No especificado</span>}
                  </div>
                )}
              </div>

              {/* Email */}
              <div className="space-y-2">
                <Label htmlFor="email" className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Mail className="w-3.5 h-3.5" />
                  Email
                </Label>
                <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/40 px-3 py-2.5 rounded-lg border border-transparent opacity-70 cursor-not-allowed">
                  {profile.email}
                  {editing && <Lock className="w-3.5 h-3.5 ml-auto flex-shrink-0" />}
                </div>
                {editing && (
                  <p className="text-xs text-muted-foreground">El email no puede modificarse.</p>
                )}
              </div>

              {/* Teléfono */}
              <div className="space-y-2">
                <Label htmlFor="phoneNumber" className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <Phone className="w-3.5 h-3.5" />
                  Teléfono
                </Label>
                {editing ? (
                  <PhoneInput
                    value={formData.phoneNumber}
                    onChange={(val) => setFormData({ ...formData, phoneNumber: val })}
                  />
                ) : (
                  <div className="flex items-center gap-2 text-sm text-foreground bg-muted/40 px-3 py-2.5 rounded-lg border border-transparent">
                    {profile.phoneNumber || <span className="text-muted-foreground italic">No especificado</span>}
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Right: Profile details */}
          <div className="p-6 space-y-5">
            <p className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">Detalles del Perfil</p>

            <div className="space-y-4">
              {editing && (
                <div className="space-y-2">
                  <Label htmlFor="profilePictureUrl" className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                    <Camera className="w-3.5 h-3.5" />
                    URL foto de perfil
                  </Label>
                  <Input
                    id="profilePictureUrl"
                    value={formData.profilePictureUrl}
                    onChange={(e) => setFormData({ ...formData, profilePictureUrl: e.target.value })}
                    maxLength={255}
                    placeholder="https://..."
                    className="rounded-lg bg-background border-border focus-visible:ring-primary"
                  />
                  <AtLimit value={formData.profilePictureUrl} max={255} />
                </div>
              )}

              {/* Bio */}
              <div className="space-y-2">
                <Label htmlFor="bio" className="text-xs font-medium text-muted-foreground flex items-center gap-1.5">
                  <FileText className="w-3.5 h-3.5" />
                  Biografía / Notas
                </Label>
                {editing ? (
                  <>
                    <Textarea
                      id="bio"
                      value={formData.bio}
                      onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                      maxLength={500}
                      size="md"
                      placeholder="Contanos sobre vos..."
                    />
                    <CharCounter value={formData.bio} max={500} />
                  </>
                ) : (
                  <div className="text-sm text-foreground bg-muted/40 px-3 py-2.5 rounded-lg border border-transparent min-h-[100px] whitespace-pre-wrap">
                    {profile.bio || <span className="text-muted-foreground italic">Sin biografía...</span>}
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Account status card */}
      <div className="overflow-hidden border rounded-lg shadow-sm bg-card border-border">
        <div className="px-6 py-4 border-b border-border flex items-center gap-3">
          <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-indigo-100 dark:bg-indigo-900/30 flex-shrink-0">
            <Shield className="w-4 h-4 text-indigo-600 dark:text-indigo-400" />
          </div>
          <div>
            <h3 className="text-sm font-semibold text-foreground">Estado de la cuenta</h3>
            <p className="text-xs text-muted-foreground">Información de acceso y seguridad</p>
          </div>
        </div>
        <div className="divide-y divide-border">

          {/* Estado */}
          <div className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3">
              {profile.isActive
                ? <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0" />
                : <XCircle className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              }
              <div>
                <p className="text-sm font-medium text-foreground">Estado</p>
                <p className="text-xs text-muted-foreground mt-0.5">Acceso al sistema</p>
              </div>
            </div>
            {profile.isActive ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400">
                <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                Activo
              </span>
            ) : (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium bg-muted text-muted-foreground">
                <span className="w-1.5 h-1.5 rounded-full bg-muted-foreground inline-block" />
                Inactivo
              </span>
            )}
          </div>

          {/* Rol asignado */}
          <div className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors">
            <div className="flex items-center gap-3">
              <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
              <div>
                <p className="text-sm font-medium text-foreground">Rol asignado</p>
                <p className="text-xs text-muted-foreground mt-0.5">Define los permisos de acceso</p>
              </div>
            </div>
            {profile.isOwner ? (
              <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400">
                <Shield className="w-3.5 h-3.5" />
                Administrador
              </span>
            ) : (
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                {roleDisplayName}
              </span>
            )}
          </div>

          {/* Nivel de acceso — solo para owners */}
          {profile.isOwner && (
            <div className="flex items-center justify-between px-6 py-4 hover:bg-muted/30 transition-colors">
              <div className="flex items-center gap-3">
                <Shield className="w-4 h-4 text-amber-500 flex-shrink-0" />
                <div>
                  <p className="text-sm font-medium text-foreground">Nivel de acceso</p>
                  <p className="text-xs text-muted-foreground mt-0.5">Permisos sobre el sistema</p>
                </div>
              </div>
              <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/30 dark:text-indigo-400">
                Acceso completo
              </span>
            </div>
          )}

        </div>
      </div>

    </div>
  );
}
